import { useTranslation } from "react-i18next";
/**
 * Canonical table-row action buttons. ALL table action buttons must use
 * these instead of building their own — keeps every "View", "Edit",
 * "Delete" icon button visually identical across the app.
 *
 * Style: secondary outlined button, icon-sized, tooltip-ready. Color comes
 * from the canonical icon variant (primary for view/edit, danger for delete).
 */

import { forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button/buttonVariants";
import {
  ViewIcon,
  EditIcon,
  DeleteIcon,
  DocumentIcon,
  DownloadIcon,
  HistoryIcon,
  ReboardIcon,
  ApproveIcon,
  RejectIcon,
  RequestChangesIcon,
} from "@/components/icons/action-icons";
import { cn } from "@/utils/cn";
import type { VariantProps } from "class-variance-authority";

type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  tooltip?: string;
  /** Override the icon size; default 16. */
  iconSize?: number;
  /** Override the button variant; defaults to the canonical per-action variant. */
  variant?: ButtonVariant;
};

const make = (
  Icon: typeof ViewIcon,
  /** `ns:key` for the fallback aria-label — resolved per render, so switching
   *  locale relabels every action button in the app. */
  defaultLabelKey: string,
  iconClass: string,
  defaultVariant: ButtonVariant = "secondary",
) =>
  forwardRef<HTMLButtonElement, Props>(
    ({ tooltip, iconSize = 16, className, variant, ...rest }, ref) => {
      const { t } = useTranslation(["common", "company"]);

      return (
        <Button
          ref={ref}
          type="button"
          variant={variant ?? defaultVariant}
          size="icon-sm"
          aria-label={tooltip ?? t(defaultLabelKey)}
          data-tooltip-id={tooltip ? "hrm-tooltip" : undefined}
          data-tooltip-content={tooltip}
          className={cn(className)}
          {...rest}
        >
          <Icon className={cn(iconClass)} size={iconSize} />
        </Button>
      );
    },
  );

export const ViewAction = make(ViewIcon, "common:buttons.view", "text-primary");
export const EditAction = make(EditIcon, "common:buttons.edit", "text-primary");
export const DeleteAction = make(
  DeleteIcon,
  "common:buttons.delete",
  "text-danger",
  "outline-danger",
);
export const DocumentsAction = make(
  DocumentIcon,
  "common:maintenance.documents",
  "text-primary",
);
export const DownloadAction = make(
  DownloadIcon,
  "common:buttons.download",
  "text-primary",
);
export const HistoryAction = make(
  HistoryIcon,
  "common:buttons.history",
  "text-primary",
);
export const ReboardAction = make(
  ReboardIcon,
  "company:employees.reboard",
  "text-success",
  "outline-success",
);
export const ApproveAction = make(
  ApproveIcon,
  "common:buttons.approve",
  "text-success",
  "outline-success",
);
export const RejectAction = make(
  RejectIcon,
  "common:buttons.reject",
  "text-danger",
  "outline-danger",
);
export const RequestChangesAction = make(
  RequestChangesIcon,
  "common:buttons.request_changes",
  "text-warning",
  "outline-warning",
);

ViewAction.displayName = "ViewAction";
EditAction.displayName = "EditAction";
DeleteAction.displayName = "DeleteAction";
DocumentsAction.displayName = "DocumentsAction";
DownloadAction.displayName = "DownloadAction";
HistoryAction.displayName = "HistoryAction";
ReboardAction.displayName = "ReboardAction";
ApproveAction.displayName = "ApproveAction";
RejectAction.displayName = "RejectAction";
RequestChangesAction.displayName = "RequestChangesAction";
